import React, { useState } from 'react';
import { 
  Box,
  Card,
  Grid,
  Tab,
  Tabs,
  Typography,
  Button,
  CircularProgress
} from '@mui/material';
import {
  AccountTreeOutlined,
  AnalyticsOutlined,
  BusinessOutlined,
  HistoryOutlined,
  StoreOutlined,
  RefreshOutlined,
  UploadFileOutlined
} from '@mui/icons-material';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
// import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import { useUser } from 'components/RDAuthContext';
import { useAdminContext } from '../../../../context/AdminContext';
import ClientSelector from '../../../../components/common/admin/ClientSelector';
import { LocationAdminProvider, useLocationAdmin } from './context/LocationAdminContext';

// Tab panels
import OverviewTab from './tabs/OverviewTab';
import StructureTab from './tabs/StructureTab';
import UnitsTab from './tabs/UnitsTab';
import LocationsTab from './tabs/LocationsTab';
import HistoryTab from './tabs/HistoryTab';
import ImportExportTab from './tabs/ImportExportTab';

// Create a query client with optimized defaults
// Export it so services can trigger cache invalidation
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,     // Consider data stale after 5 minutes
      gcTime: 30 * 60 * 1000,        // Keep in cache for 30 minutes (was cacheTime)
      retry: 1,                       // Only retry once on failure
      refetchOnWindowFocus: false,   // Don't refetch on window focus
      refetchOnMount: false,          // Don't refetch on component mount if data exists
    },
  },
});

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`location-admin-tabpanel-${index}`}
      aria-labelledby={`location-admin-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

// Inner component that uses the context
function LocationAdminContent() {
  const [tabValue, setTabValue] = useState(0);
  const { user } = useUser();
  const {
    selectedClient,
    setSelectedClient,
    isAppAdmin,
    availableClients,
    loading: adminLoading,
    error: adminError
  } = useAdminContext();
  const {
    isLoading,
    isRefetching,
    error,
    refetch
  } = useLocationAdmin();

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    // Tab switch handled
  };

  const handleRefresh = async () => {
    // Manual refresh triggered - refresh ALL queries
    await Promise.all([
      queryClient.refetchQueries({
        queryKey: ['locationAdmin', selectedClient]
      }),
      queryClient.refetchQueries({
        queryKey: ['orgUnitsData', selectedClient]
      }),
      queryClient.refetchQueries({
        queryKey: ['hierarchyStructure', selectedClient]
      })
    ]);
  };

  const tabs = [
    { label: 'Overview', icon: <AnalyticsOutlined /> },
    { label: 'Structure', icon: <AccountTreeOutlined /> },
    { label: 'Units', icon: <BusinessOutlined /> },
    { label: 'Locations', icon: <StoreOutlined /> },
    { label: 'History', icon: <HistoryOutlined /> },
    { label: 'Import/Export', icon: <UploadFileOutlined /> }
  ];

  if (error) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <Box pt={3} pb={3}>
          <Card>
            <Box p={3}>
              <Typography color="error">
                Error loading Location Admin data: {error.message}
              </Typography>
              <Button onClick={handleRefresh} sx={{ mt: 2 }}>
                Retry
              </Button>
            </Box>
          </Card>
        </Box>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Box pt={3} pb={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <Box p={3}>
                <Box mb={3} display="flex" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography variant="h3" fontWeight="bold">
                      Location Admin
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {user?.organizationName || 'Organization'}
                    </Typography>
                  </Box>
                  <Box display="flex" gap={2} alignItems="center">
                    {isAppAdmin && (
                      <ClientSelector
                        value={selectedClient}
                        onChange={setSelectedClient}
                        clients={availableClients}
                        loading={adminLoading}
                        error={adminError}
                      />
                    )}
                    <Button
                      variant="outlined"
                      startIcon={isRefetching ? <CircularProgress size={16} /> : <RefreshOutlined />}
                      onClick={handleRefresh}
                      disabled={isRefetching}
                      sx={{
                        borderColor: 'divider',
                        color: 'text.primary',
                        '&:hover': {
                          borderColor: 'primary.main',
                          backgroundColor: 'action.hover'
                        }
                      }}
                    >
                      {isRefetching ? 'Refreshing...' : 'Refresh Data'}
                    </Button>
                  </Box>
                </Box>

                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                  <Tabs 
                    value={tabValue} 
                    onChange={handleTabChange}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{
                      '& .MuiTab-root': {
                        minHeight: 56,
                        textTransform: 'none',
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        '& .MuiSvgIcon-root': {
                          color: '#7C3AED',
                          fontSize: '1.5rem'
                        }
                      },
                      '& .Mui-selected': {
                        backgroundColor: 'rgba(124, 58, 237, 0.08)',
                        '& .MuiSvgIcon-root': {
                          color: '#7C3AED'
                        }
                      }
                    }}
                  >
                    {tabs.map((tab, index) => (
                      <Tab
                        key={index}
                        icon={tab.icon}
                        label={tab.label}
                        iconPosition="start"
                        id={`location-admin-tab-${index}`}
                        aria-controls={`location-admin-tabpanel-${index}`}
                      />
                    ))}
                  </Tabs>
                </Box>

                {isLoading ? (
                  <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <>
                    <TabPanel value={tabValue} index={0}>
                      <OverviewTab 
                        onSwitchToStructure={() => setTabValue(1)}
                        onSwitchToUnits={() => setTabValue(2)}
                        onSwitchToLocations={() => setTabValue(3)}
                        onSwitchToHistory={() => setTabValue(4)}
                      />
                    </TabPanel>
                    <TabPanel value={tabValue} index={1}>
                      <StructureTab />
                    </TabPanel>
                    <TabPanel value={tabValue} index={2}>
                      <UnitsTab />
                    </TabPanel>
                    <TabPanel value={tabValue} index={3}>
                      <LocationsTab />
                    </TabPanel>
                    <TabPanel value={tabValue} index={4}>
                      <HistoryTab />
                    </TabPanel>
                    <TabPanel value={tabValue} index={5}>
                      <ImportExportTab onDataChange={refetch} />
                    </TabPanel>
                  </>
                )}
              </Box>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </DashboardLayout>
  );
}

// Main component with providers
function LocationAdmin() {
  return (
    <QueryClientProvider client={queryClient}>
      <LocationAdminProvider>
        <LocationAdminContent />
      </LocationAdminProvider>
      {/* {process.env.NODE_ENV === 'development' && <ReactQueryDevtools initialIsOpen={false} />} */}
    </QueryClientProvider>
  );
}

export default LocationAdmin;